import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Alert, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AppointmentApi, { Appointment as Appt } from "../api/AppointmentApi";
import ApiInstance from "../api/ApiInstance";
import { eur } from "../constants/funnel";

// Attest-endpoints leveren HTML (printen → PDF) achter cookie-auth: als blob
// ophalen en in een nieuw tabblad openen.
async function openAttest(path: string): Promise<void> {
  const res = await ApiInstance.getApi().get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data as Blob], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function Appointment() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = !!(params.get("new") || params.get("paid"));

  const [appt, setAppt] = useState<Appt | null>(null);
  const [error, setError] = useState(false);
  const [room, setRoom] = useState<{ roomUrl: string; provider: string } | null>(null);
  const [roomMsg, setRoomMsg] = useState("");
  const [attestMsg, setAttestMsg] = useState("");
  const [attestFund, setAttestFund] = useState("");

  useEffect(() => {
    if (id) AppointmentApi.get(id).then((a) => { setAppt(a); if (a.fundCode) setAttestFund(a.fundCode); }).catch(() => setError(true));
  }, [id]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Refund outcome of the cancellation (WER VI.47/VI.50): shown after cancelling.
  const [cancelOutcome, setCancelOutcome] = useState<"refunded" | "none" | "failed" | null>(null);
  const doCancel = async () => {
    setCancelling(true);
    try {
      const res = await AppointmentApi.cancel(id!);
      setCancelOutcome(res.refund?.status ?? "none");
      setAppt(await AppointmentApi.get(id!));
    } finally {
      setCancelling(false);
      setConfirmOpen(false);
    }
  };

  const join = async () => {
    setRoomMsg("");
    try {
      setRoom(await AppointmentApi.room(id!));
    } catch {
      // Room errors (too early / not confirmed / ended) → show the localized message.
      setRoomMsg(t("appointment.roomNotReady"));
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h2" sx={{ mb: 2 }}>{t("appointment.notFound")}</Typography>
        <Button variant="contained" onClick={() => navigate("/")}>{t("common.backHome")}</Button>
      </Container>
    );
  }
  if (!appt) {
    return (
      <Box sx={{ minHeight: "50dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  const start = new Date(appt.start);
  const dateStr = new Intl.DateTimeFormat(i18n.language, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(start);
  const confirmed = appt.status === "confirmed";

  return (
    <Box sx={{ bgcolor: "background.default", pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 5, sm: 8 } }}>
        {isNew && confirmed && (
          <Alert icon={<CheckCircleRoundedIcon />} severity="success" sx={{ mb: 3 }}>
            {t("appointment.success")}
          </Alert>
        )}

        {cancelOutcome && (
          <Alert severity={cancelOutcome === "failed" ? "warning" : "info"} sx={{ mb: 3 }}>
            {cancelOutcome === "refunded"
              ? t("appointment.cancelledRefund")
              : cancelOutcome === "failed"
                ? t("appointment.cancelledRefundFailed")
                : t("appointment.cancelledNoPayment")}
          </Alert>
        )}

        <Typography variant="h1" sx={{ fontSize: { xs: "2rem", sm: "2.5rem" }, mb: 1 }}>
          {t(`booking.type.${appt.type}`)}
        </Typography>
        <Typography sx={{ fontSize: "1.1rem", textTransform: "capitalize" }}>{dateStr}</Typography>

        <Box sx={{ mt: 3, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {appt.status === "cancelled" ? (
            <Chip label={t("appointment.statusCancelled")} sx={{ fontWeight: 600 }} />
          ) : (
            <Chip label={confirmed ? t("appointment.statusConfirmed") : t("appointment.statusPending")}
              color={confirmed ? "success" : "warning"} sx={{ fontWeight: 600 }} />
          )}
          <Chip label={appt.priceCents === 0 ? t("booking.free") : eur(appt.priceCents)} variant="outlined" />
        </Box>

        {appt.reimbursementSnapshot && appt.reimbursementSnapshot.perSessionCents > 0 && (
          <Box sx={{ mt: 3, p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Typography sx={{ color: "primary.dark", m: 0 }}>
              {t("appointment.reimbursement", { amount: eur(appt.reimbursementSnapshot.perSessionCents), youPay: eur(appt.reimbursementSnapshot.youPayCents) })}
            </Typography>
          </Box>
        )}

        {/* Terugbetalingsattest: zelf-betaalde, verrichte sessie. De API
            bewaakt de voorwaarden (verricht, zelf betaald, geen werkgever-
            financiering); werkgever-gefinancierde sessies hebben hier
            priceCents 0, dus de knop verschijnt dan niet. */}
        {confirmed && appt.priceCents > 0 && start < new Date() && (
          <Box sx={{ mt: 4, p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("appointment.attestTitle")}</Typography>
            <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mb: 2 }}>{t("appointment.attestNote")}</Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button variant="outlined" startIcon={<ReceiptLongRoundedIcon />} onClick={() => { setAttestMsg(""); openAttest(`/appointments/${appt._id}/attest`).catch(() => setAttestMsg(t("appointment.attestUnavailable"))); }}>
                {t("appointment.attestDownload")}
              </Button>
              <Button variant="text" onClick={() => { setAttestMsg(""); openAttest(`/appointments/attest/verzamel?psychologistId=${appt.psychologistId}&year=${start.getFullYear()}`).catch(() => setAttestMsg(t("appointment.attestUnavailable"))); }}>
                {t("appointment.attestYear", { year: start.getFullYear() })}
              </Button>
            </Box>
            {/* Vooraf ingevuld formulier naar het model van het eigen fonds
                (Solidaris/NZVL eisen dat formeel; voor de rest is het gemak). */}
            <Box sx={{ mt: 2, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <TextField select size="small" label={t("appointment.attestFundChoose")} value={attestFund}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAttestFund(e.target.value)} sx={{ minWidth: 260 }}>
                <MenuItem value="cm">CM (Christelijke Mutualiteit)</MenuItem>
                <MenuItem value="helan">Helan</MenuItem>
                <MenuItem value="solidaris">Solidaris</MenuItem>
                <MenuItem value="lm">Liberale Mutualiteit</MenuItem>
                <MenuItem value="nzvl">Neutraal Ziekenfonds Vlaanderen</MenuItem>
                <MenuItem value="vnz">Vlaams & Neutraal Ziekenfonds</MenuItem>
              </TextField>
              <Button variant="outlined" disabled={!attestFund} onClick={() => { setAttestMsg(""); openAttest(`/appointments/attest/fonds?psychologistId=${appt.psychologistId}&year=${start.getFullYear()}&fundCode=${attestFund}`).catch(() => setAttestMsg(t("appointment.attestUnavailable"))); }}>
                {t("appointment.attestFundForm")}
              </Button>
            </Box>
            {attestMsg && <Typography sx={{ mt: 1.5, fontSize: ".9rem", color: "text.secondary" }}>{attestMsg}</Typography>}
          </Box>
        )}

        {/* Video */}
        {confirmed && (
          <Box sx={{ mt: 5 }}>
            {!room && (
              <>
                <Button variant="contained" size="large" startIcon={<VideocamRoundedIcon />} onClick={join}>
                  {t("appointment.joinVideo")}
                </Button>
                {roomMsg && <Typography sx={{ mt: 2, color: "text.secondary" }}>{roomMsg}</Typography>}
              </>
            )}
            {room && room.provider === "stub" && (
              <Alert severity="info">{t("appointment.stubNote")}</Alert>
            )}
            {room && room.provider !== "stub" && (
              <Box
                component="iframe"
                title="Carefunded video"
                src={room.roomUrl}
                allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
                sx={{ width: "100%", height: "70vh", border: 0, borderRadius: "var(--radius-lg)" }}
              />
            )}
          </Box>
        )}

        {/* Cancel (either participant, while upcoming) */}
        {(appt.status === "confirmed" || appt.status === "pending_payment") && new Date(appt.start) > new Date() && (
          <Box sx={{ mt: 4 }}>
            <Button variant="text" color="error" onClick={() => setConfirmOpen(true)}>{t("appointment.cancel")}</Button>
          </Box>
        )}
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("appointment.cancelConfirmTitle")}</DialogTitle>
        <DialogContent><Typography sx={{ m: 0 }}>{t("appointment.cancelConfirmBody")}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t("appointment.cancelDismiss")}</Button>
          <Button color="error" variant="contained" onClick={doCancel} disabled={cancelling}>{t("appointment.cancelConfirm")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
